import { useEffect } from 'react';

export interface HeadMetaInput {
  description?: string;
  canonical?: string;
  /** Overrides index.html's site-wide `robots` meta, e.g. 'noindex, nofollow'. */
  robots?: string;
  og?: {
    title?: string;
    description?: string;
    type?: string;
    url?: string;
    image?: string;
  };
  twitter?: {
    card?: string;
    title?: string;
    description?: string;
    image?: string;
  };
  /** Optional structured data — JSON-LD object(s) injected as
   *  `<script type="application/ld+json">` blocks. */
  jsonLd?: object | object[];
}

type Cleanup = () => void;

function ensureMeta(name: string, attr: 'name' | 'property', value: string): Cleanup {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${name}"]`,
  );
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  const previous = el.content;
  el.content = value;
  return () => {
    if (created) el!.remove();
    else el!.content = previous;
  };
}

function ensureCanonical(href: string): Cleanup {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  const previous = el.href;
  el.href = href;
  return () => {
    if (created) el!.remove();
    else el!.href = previous;
  };
}

function injectJsonLd(payload: object | object[]): Cleanup {
  const items = Array.isArray(payload) ? payload : [payload];
  const scripts: HTMLScriptElement[] = items.map((item, i) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.dataset.headMetaIndex = String(i);
    s.textContent = JSON.stringify(item);
    document.head.appendChild(s);
    return s;
  });
  return () => scripts.forEach((s) => s.remove());
}

/**
 * Set per-page <meta>, <link rel="canonical">, OG/Twitter cards, and
 * optional JSON-LD structured data for the lifetime of the component.
 *
 * Restores previous values on unmount. Avoids react-helmet-async.
 */
export function useHeadMeta(input: HeadMetaInput): void {
  useEffect(() => {
    const cleanups: Cleanup[] = [];

    if (input.description) {
      cleanups.push(ensureMeta('description', 'name', input.description));
    }
    if (input.robots) {
      cleanups.push(ensureMeta('robots', 'name', input.robots));
    }
    if (input.canonical) {
      cleanups.push(ensureCanonical(input.canonical));
    }
    if (input.og?.title) cleanups.push(ensureMeta('og:title', 'property', input.og.title));
    if (input.og?.description) cleanups.push(ensureMeta('og:description', 'property', input.og.description));
    if (input.og?.type) cleanups.push(ensureMeta('og:type', 'property', input.og.type));
    if (input.og?.url) cleanups.push(ensureMeta('og:url', 'property', input.og.url));
    if (input.og?.image) cleanups.push(ensureMeta('og:image', 'property', input.og.image));
    if (input.twitter?.card) cleanups.push(ensureMeta('twitter:card', 'name', input.twitter.card));
    if (input.twitter?.title) cleanups.push(ensureMeta('twitter:title', 'name', input.twitter.title));
    if (input.twitter?.description) cleanups.push(ensureMeta('twitter:description', 'name', input.twitter.description));
    if (input.twitter?.image) cleanups.push(ensureMeta('twitter:image', 'name', input.twitter.image));
    if (input.jsonLd) cleanups.push(injectJsonLd(input.jsonLd));

    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(input)]);
}
